import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import SpendSparkline from "./SpendSparkline";
import MoMChip from "./MoMChip";
import { humanEUR } from "./format";

// TotalSpendTile is the lead KPI — the anchor figure (month-to-date actual), the projected
// month-end landing (with its honest range/confidence on hover), the delta vs the previous month,
// and the actual-vs-forecast trend — in the same tile frame as ControlKpiTile so the row stays
// uniform. It also carries the spend-forecast risk context (breach probability, cold-start
// provisional note) that only appears in the states that have it. Replaces the old loose header
// spend line and the empty AI tile.
const TotalSpendTile = ({
  actual,
  forecast,
  projTip,
  momPct,
  prevActual,
  prevLabel,
  breachProbability = null,
  breachProvisional = false,
  expectedOverage = null,
  provisional = false,
  provisionalReason,
  trend = [],
  forecastTrend = [],
  cap = null,
  href = "/cost-analyzer",
  onViewTotal,
}) => {
  const projected = Number(forecast) > 0;
  return (
    <Link to={href} className="flex min-w-[150px] flex-col rounded-card border border-card-bd bg-card px-4 py-3 hover:border-border">
      <div className="text-xs font-medium uppercase tracking-wide text-text-2">Total spend</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-text">{humanEUR(actual)}</div>
      {projected ? (
        <div
          className="mt-1 text-xs leading-snug text-text-2"
          data-tooltip-id={projTip ? "instantTooltip" : undefined}
          data-tooltip-content={projTip || undefined}
        >
          projected {humanEUR(forecast)} month-end
        </div>
      ) : (
        <div className="mt-1 text-xs leading-snug text-text-2">month-to-date</div>
      )}
      <div className="mt-0.5">
        <MoMChip momPct={momPct} prevActual={prevActual} prevLabel={prevLabel} />
      </div>
      {breachProbability != null ? (
        <div className="mt-1 text-xs leading-snug text-text-2">
          {breachProvisional ? "provisional: " : ""}~{Math.round(breachProbability * 100)}% chance of exceeding budget by month-end
          {expectedOverage != null ? ` · ~${humanEUR(expectedOverage)} expected overage` : ""}
        </div>
      ) : null}
      {provisional ? (
        <div className="mt-1 text-xs leading-snug text-warning">Provisional — {provisionalReason || "based on your other sources"}</div>
      ) : null}
      <div className="mt-2">
        <SpendSparkline data={trend} forecast={forecastTrend} cap={cap} />
      </div>
      {onViewTotal ? (
        <div className="mt-auto pt-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onViewTotal();
            }}
            className="text-xs font-medium text-primary hover:text-primary-hover"
          >
            View total
          </button>
        </div>
      ) : null}
    </Link>
  );
};

TotalSpendTile.propTypes = {
  actual: PropTypes.number,
  forecast: PropTypes.number,
  projTip: PropTypes.string,
  momPct: PropTypes.number,
  prevActual: PropTypes.number,
  prevLabel: PropTypes.string,
  breachProbability: PropTypes.number,
  breachProvisional: PropTypes.bool,
  expectedOverage: PropTypes.number,
  provisional: PropTypes.bool,
  provisionalReason: PropTypes.string,
  trend: PropTypes.array,
  forecastTrend: PropTypes.array,
  cap: PropTypes.number,
  href: PropTypes.string,
  onViewTotal: PropTypes.func,
};

export default TotalSpendTile;
